
import { Vue, Component, Prop, toNative } from 'vue-facing-decorator';
import EmailTemplateRenderer from './EmailTemplateRenderer.vue';
import loading from '@/components/Common/loading.vue';
import { FetchData, Store } from '@/main';
import { EmailTemplate } from '@/entities/Invite/EmailTemplateViewModel';
import EmailTemplatesModel from '@/entities/Invite/EmailTemplatesModel';
import { minLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { reactive } from 'vue';
import { BrowserSettingsInfo } from '@/entities/BrowserSettingsInfo';
import { StoreHelper } from '@/store/StoreHelper';
import moment from 'moment';
import Toaster from '@/components/Common/Toaster.vue';
import ConfigureEmailsHelper from '@/helper/ConfigureEmailsHelper';
import ModalPopup from '@/components/Common/ModalPopup.vue';

@Component({
  components: { EmailTemplateRenderer, loading, Toaster, ModalPopup },
})
class EmailTemplates extends Vue {
  @Prop() allEmailTemplates!: EmailTemplate[];

  // Icon images on the screen
  private editIcon = require('@/assets/Images/Invite/editIcon.svg');
  private tickIcon = require('@/assets/Images/Invite/tickIcon.svg');
  private crossIcon = require('@/assets/Images/Invite/crossIcon.svg');
  private cardCrossIcon = require('@/assets/Images/Invite/modal-cross-icon.svg');
  private trashIcon = require('@/assets/Images/Invite/trash-bin.svg');

  // flags for preview, loading and modal
  private showPreview = false;
  private showLoading = false;
  private showToaster = false;
  private isSmallTab = false;

  // Common Helper class
  private storeHelper: StoreHelper = new StoreHelper(Store);

  // Variable that stores the preview or loaded of the template
  private workingTemplate: EmailTemplate = EmailTemplate.createEmpty();

  // Modal text, loader border color and template name variables
  private loaderBorderColor = '';
  private toasterMessage = '';
  private toasterDefaultTimeout = ConfigureEmailsHelper.ToasterTimeOut;
  private loaderDefaultTimeout = ConfigureEmailsHelper.LoaderTimeOut;
  private templateNameMinLength = ConfigureEmailsHelper.TemplateNameMinLength;
  private currentModal = ConfigureEmailsHelper.DeleteModal;

  // Variables for storing the index of the curent template card
  private editingIdx: number | null = null;
  private selectedTemplateId: number | null = null;

  // Modal Pop up properties
  private modalPopupVisible = false;
  private modalPopupHeading = '';
  private modalPopupBodyHeading = '';
  private showCloseButton = true;
  private showCancelButton = true;
  private modalPopupContent = '';
  private buttonText = '';

  // Template name validation logic block
  private state = reactive({
    workingTemplate: EmailTemplate.createEmpty(),
  });

  // Vue Validation logic block
  private rules = {
    workingTemplate: {
      TemplateData : {
        TemplateName : {
          required,
          minLength: minLength(1),
        },
      },
    },
  };
  private status(validation: {$error: any; $dirty: any}){
    return {
      error: validation.$error,
      dirty: validation.$dirty,
    };
  }

  private v$ = useVuelidate(this.rules, this.state);

  // Mounted life cycle hook
  private mounted(){
    if(window.innerWidth <= 1200){
      this.isSmallTab = true;
    }else{
      this.isSmallTab = false;
    }
  }

  // Check browsers Time Format
  private CheckTimeFormat(): boolean {
    const browsersettings: BrowserSettingsInfo = this.storeHelper.GetBrowserSettingsFromStore();
    return browsersettings.isFullDayTimeFormat;
  }

  // Edit Template name handler
  private EditTemplateName(idx: number, template: EmailTemplatesModel){
    this.editingIdx = idx;
    this.workingTemplate.TemplateData = {...template};
    this.state.workingTemplate.TemplateData = this.workingTemplate.TemplateData;
    return this.GetTemplateLabel(template, idx) || '';
  }

  // Delete the given Template
  private DeleteTemplateModal(templateId: number){
    this.modalPopupVisible = true;
    this.currentModal = ConfigureEmailsHelper.DeleteModal;
    this.selectedTemplateId = templateId;
    this.showCancelButton = true;
    this.modalPopupHeading = this.$t('Delete').toString();
    this.modalPopupContent = this.$t('Invite.ConfigureEmails.DeleteTemplate').toString();
    this.buttonText = this.$t('YesButton').toString();
  }

  private ConfirmClick(confirmed: boolean) {
    this.modalPopupVisible = false;
    if(confirmed){
      if(this.currentModal === ConfigureEmailsHelper.ApplyModal) {
        this.ApplyTemplate();
      } else if(this.currentModal === ConfigureEmailsHelper.DeleteModal){
        this.DeleteTemplate();
      }else{
        // Do nothing
      }
    }
    this.buttonText = '';
  }

  // Apply Template function to select and redirect to edit screen
  private ApplyTemplate(){
    this.showLoading = true;
    this.modalPopupVisible = false;

    setTimeout(()=> {
      this.$emit('redirect-to-edit-pane', this.workingTemplate);
      this.showLoading = false;
    }, 1000);
  }

  // Function to delete the selected template
  private DeleteTemplate(){
    this.showLoading = true;
    FetchData('/Registration/DeleteEmailTemplate', JSON.stringify({
      templateId: this.selectedTemplateId,
    })).then(()=>{
      this.showLoading = false;
      this.$emit('remove-template', this.selectedTemplateId);
      this.ShowToasterAndLoader(this.$t('Invite.ConfigureEmails.TemplateDeletionToast').toString());
    }).catch(()=>{
      this.showLoading = false;
    });
    this.modalPopupVisible = false;

  }

  // Common function for showing and hiding toaster message and loader
  private ShowToasterAndLoader(toasterMessage: string) {
    this.toasterMessage = toasterMessage;
    this.showToaster = true;
    setTimeout(() => {
      this.showLoading = false;
      setTimeout(() => {
        this.showToaster = false;
      }, this.toasterDefaultTimeout);
    }, this.loaderDefaultTimeout);
  }

  // Function for formatting modified Date for display
  private GetFormattedDate(date: string){
    const format = this.CheckTimeFormat() ? 'MMM D, YYYY, HH:mm' : 'MMM D, YYYY, hh:mm A';
    return date ? moment(date).format(format) : '';
  }

  // Discard Template name changes
  private CancelTemplateNameChanges(){
    this.editingIdx = null;
  }

  // Function checking whether the template name is unique or not
  private TemplateExist(input: string, currentId: number): boolean {
    const foundExist = this.allEmailTemplates.some((template: any) =>
      template.TemplateData.TemplateName.toLowerCase() === input.toLowerCase() &&
      template.TemplateData.Id !== currentId,
    );
    return foundExist;
  }

  // Function saving new template name
  private ChangeTemplateName(template: any){
    template.$touch();
    this.currentModal = ConfigureEmailsHelper.ErrorModal;
    this.modalPopupHeading = this.$t('Invite.ConfigureEmails.Error').toString();
    this.showCancelButton = false;

    if(this.workingTemplate.TemplateData.TemplateName.length === 0){
      this.modalPopupVisible = true;
      this.modalPopupContent = this.$t('Invite.ConfigureEmails.TemplateNameRequired').toString();
      this.buttonText = this.$t('Button.Close').toString();
    } else if(this.TemplateExist(this.workingTemplate.TemplateData.TemplateName, this.workingTemplate.TemplateData.Id)){
      this.modalPopupVisible = true;
      this.modalPopupContent = this.$t('Invite.ConfigureEmails.TemplateNameWarning').toString();
      this.buttonText = this.$t('Button.Close').toString();
    } else{
      this.showLoading = true;
      FetchData('/Registration/UpdateTemplateName', JSON.stringify({
        templateId : this.workingTemplate.TemplateData.Id,
        templateName : this.workingTemplate.TemplateData.TemplateName.trim(),
      })).then(() => {
        this.$emit('rename-template', this.workingTemplate.TemplateData.Id, this.workingTemplate.TemplateData.TemplateName.trim());
        this.ShowToasterAndLoader(this.$t('Invite.ConfigureEmails.TemplateNameChangeToast').toString());
      }).catch(() => {
        this.showLoading = false;
      });
    }

    this.CancelTemplateNameChanges();
  }

  // Function for opening template preview
  private OpenTemplatePreview(template: EmailTemplate) {
    this.workingTemplate = template;
    this.showPreview = true;
  }

  // Function for closing preview modal
  private ClosePreviewModal() {
    this.showPreview = false;
  }

  // Get Default Name or Template name for the Templates
  private GetTemplateLabel(template: EmailTemplatesModel, idx: number) {
    if(template.IsDefault) {
      return `${this.$t('Invite.ConfigureEmails.DefaultTemplates').toString()} ${idx + 1}`;
    } else {
      return template.TemplateName;
    }
  }
  // Apply Template modal handler
  private OpenApplyTemplateModal(template: EmailTemplate){
    this.workingTemplate = template;
    this.modalPopupVisible = true;
    this.showCancelButton = true;
    this.currentModal = ConfigureEmailsHelper.ApplyModal;
    this.modalPopupHeading = this.$t('Invite.ConfigureEmails.ApplyTemplate').toString();
    this.modalPopupContent = this.$t('Invite.ConfigureEmails.ChangeTemplateText').toString();
    this.buttonText = this.$t('Button.Ok');
  }

}
export default toNative(EmailTemplates);
